import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import Logger from "../../../services/Logger";
import { useNavigate, useSearchParams } from "react-router-dom";
import validator from "validator";
import {
    APP_SITE_REGISTRY,
    CONTACT_APP_IDS,
    CONTACT_TYPE,
    parseContactAppParam,
    parseContactTypeParam,
    resolveAppIconSrc,
} from "../../../config/appSiteLinks";
import { SURFACE, TEXT } from "../../../dark-theme/palette";
import { trackEvent } from "../../../services/GoogleAnalytics";
import AuthService from "../../../services/AuthService";
import UserService from "../../../services/UserService";
import CustomSnackbar from "../../components/CustomSnackbar";
import { usePagesTranslation } from "../../locales";
import { Heading, Subtitle } from "./InfoSection.elements";
import { default as UserContactService } from "./UserContactService";

const MAX_SUBJECT_WORDS = 50;
const MAX_CONTENT_WORDS = 500;

function countWords(text) {
    if (!text || typeof text !== "string") return 0;
    return text.trim().split(/\s+/).filter(Boolean).length;
}

/** Keeps at most maxWords whitespace-separated tokens. Preserves trailing spaces while typing. */
function truncateToWordLimit(text, maxWords) {
    if (text == null) return "";
    if (maxWords <= 0) return "";
    const parts = text.trim().split(/\s+/).filter(Boolean);
    if (parts.length <= maxWords) return text;
    return parts.slice(0, maxWords).join(" ");
}

const commonSx = {
    "& label.Mui-focused": {
        color: "#3BA666",
    },
    "& .MuiInput-underline:after": {
        borderBottomColor: "#3BA666",
    },
    "& .MuiOutlinedInput-root": {
        "& fieldset": {
            borderColor: "#7EAF91",
        },
        "&:hover fieldset": {
            borderColor: "#3BA666",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#3BA666",
        },
    },
};

export default function UserContactMain() {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [contactType, setContactType] = useState(() => {
        const app = parseContactAppParam(searchParams.get("app"));
        return app ? CONTACT_TYPE.FEEDBACK : parseContactTypeParam(searchParams.get("type"));
    });
    const [contactApp, setContactApp] = useState(() => parseContactAppParam(searchParams.get("app")));

    useEffect(() => {
        trackEvent('entered', { name: 'UserContactMain' });
    }, []);

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [subject, setSubject] = useState("");
    const [content, setContent] = useState("");

    const [errFirstName, setErrFirstName] = useState(false);
    const [errLastName, setErrLastName] = useState(false);
    const [errEmail, setErrEmail] = useState(false);
    const [errSubject, setErrSubject] = useState(false);
    const [errContent, setErrContent] = useState(false);
    const [errApp, setErrApp] = useState(false);

    const selectedApp = useMemo(() => APP_SITE_REGISTRY[contactApp] ?? null, [contactApp]);
    const isFeedback = contactType === CONTACT_TYPE.FEEDBACK;

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState(false);
    const [message, setMessage] = useState("");

    useEffect(() => {
        const loadUserData = async () => {
            try {
                const authStatus = await AuthService.isAuthenticated();
                setIsAuthenticated(authStatus);
                
                if (authStatus) {
                    const userInfo = await UserService.getUserInfo();
                    setUser(userInfo);
                    if (userInfo?.cloudUser) {
                        // Pre-fill email if available
                        if (userInfo.cloudUser.email) {
                            setEmail(userInfo.cloudUser.email);
                        }
                        // Pre-fill name if available - check both locations
                        if (userInfo.cloudUser.firstName || userInfo.cloudUser.details?.firstName) {
                            setFirstName(userInfo.cloudUser.firstName || userInfo.cloudUser.details.firstName);
                        }
                        if (userInfo.cloudUser.lastName || userInfo.cloudUser.details?.lastName) {
                            setLastName(userInfo.cloudUser.lastName || userInfo.cloudUser.details.lastName);
                        }
                    }
                }
            } catch (error) {
                Logger.error('Error loading user data:', error);
                setIsAuthenticated(false);
            }
        };
        
        loadUserData();
    }, []);

    useEffect(() => {
        if (!isFeedback || !selectedApp) return;
        setSubject((prev) => {
            if (prev.trim()) return prev;
            return `${selectedApp.displayName} feedback`;
        });
    }, [isFeedback, selectedApp]);

    const handleSend = () => {
        // Validate fields based on authentication status
        if (!isAuthenticated) {
            if (validator.isEmpty(firstName)) {
                setErrFirstName(true);
                return;
            } else {
                setErrFirstName(false);
            }

            if (validator.isEmpty(lastName)) {
                setErrLastName(true);
                return;
            } else {
                setErrLastName(false);
            }

            if (validator.isEmpty(email) || !validator.isEmail(email)) {
                setErrEmail(true);
                return;
            } else {
                setErrEmail(false);
            }
        } else {
            // For logged-in users, use pre-filled data or user info
            // Email should always be available for logged-in users
            const finalEmail = email || user?.cloudUser?.email || "";
            
            if (!finalEmail || !validator.isEmail(finalEmail)) {
                handleSnackBar("error", t("profile.contact.err-email-missing"));
                return;
            }
            
            // First and last name might not be in profile, use defaults if needed
            // Check both cloudUser.firstName and cloudUser.details.firstName
            const finalFirstName = firstName || user?.cloudUser?.firstName || user?.cloudUser?.details?.firstName || "User";
            const finalLastName = lastName || user?.cloudUser?.lastName || user?.cloudUser?.details?.lastName || "";
            
            setFirstName(finalFirstName);
            setLastName(finalLastName);
            setEmail(finalEmail);
        }

        if (isFeedback && !contactApp) {
            setErrApp(true);
            return;
        } else {
            setErrApp(false);
        }

        if (validator.isEmpty(subject)) {
            setErrSubject(true);
            return;
        } else {
            setErrSubject(false);
        }

        if (validator.isEmpty(content)) {
            setErrContent(true);
            return;
        } else {
            setErrContent(false);
        }

        // Get final values - for logged-in users, these are already set above
        const finalFirstName = firstName || user?.cloudUser?.firstName || user?.cloudUser?.details?.firstName || "User";
        const finalLastName = lastName || user?.cloudUser?.lastName || user?.cloudUser?.details?.lastName || "";
        const finalEmail = email || user?.cloudUser?.email || "";

        UserContactService.postContact("", finalFirstName, finalLastName, finalEmail, subject, content, {
            type: contactType,
            ...(isFeedback && contactApp ? { app: contactApp } : {}),
        })
            .then(() => {
                const params = new URLSearchParams();
                params.set("type", contactType);
                if (isFeedback && contactApp) {
                    params.set("app", contactApp);
                }
                navigate(
                    `/contact/detail/${encodeURIComponent(finalFirstName || "User")}?${params.toString()}`
                );
            })
            .catch((e) => {
                const errorMessage = e?.message || e?.toString() || t("profile.contact.err-send-failed");
                handleSnackBar("error", errorMessage);
            });
    };

    const handleSnackBar = (severity, message) => {
        setOpenSnackbar(true);
        setSeverity(severity);
        setMessage(message);
    };

    return (
        <Box sx={{
            width: "100%",
            minHeight: "100vh",
            py: 4
        }}>
            <Box sx={{
                maxWidth: "900px",
                mx: "auto",
                px: 3
            }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3, color: "#3BA666" }}>
                    {t("profile.contact.contact-us")}
                </Typography>

                <Grid container spacing={4} sx={{ minHeight: isAuthenticated ? "calc(100vh - 200px)" : "auto" }}>
                    <Grid
                    size={{ xs: 12, md: 5 }}
                    sx={{
                        color: TEXT.primary,
                        backgroundColor: SURFACE.card,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        p: 3,
                        borderRadius: 2,
                        boxShadow: 1,
                        minHeight: isAuthenticated ? "calc(100vh - 200px)" : "auto",
                    }}
                >
                    {!isAuthenticated && (
                        <>
                            <TextField
                                id="txtFirstName"
                                size="small"
                                label={t("profile.contact.first-name")}
                                variant="outlined"
                                fullWidth
                                required
                                value={firstName}
                                onChange={(e) => {
                                    setFirstName(e.target.value);
                                    setErrFirstName(false);
                                }}
                                error={errFirstName}
                                sx={commonSx}
                            />
                            <TextField
                                id="txtLastName"
                                size="small"
                                label={t("profile.contact.last-name")}
                                variant="outlined"
                                fullWidth
                                required
                                value={lastName}
                                onChange={(e) => {
                                    setLastName(e.target.value);
                                    setErrLastName(false);
                                }}
                                error={errLastName}
                                sx={commonSx}
                            />
                            <TextField
                                id="txtEmail"
                                size="small"
                                label={t("profile.contact.email")}
                                variant="outlined"
                                fullWidth
                                required
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setErrEmail(false);
                                }}
                                error={errEmail}
                                sx={commonSx}
                            />
                        </>
                    )}
                    <FormControl size="small" fullWidth sx={commonSx}>
                        <InputLabel id="contact-type-label">{t("profile.contact.type")}</InputLabel>
                        <Select
                            labelId="contact-type-label"
                            id="selContactType"
                            label={t("profile.contact.type")}
                            value={contactType}
                            onChange={(e) => {
                                const nextType = e.target.value;
                                setContactType(nextType);
                                if (nextType !== CONTACT_TYPE.FEEDBACK) {
                                    setContactApp("");
                                    setErrApp(false);
                                }
                            }}
                        >
                            <MenuItem value={CONTACT_TYPE.GENERAL}>{t("profile.contact.type-general")}</MenuItem>
                            <MenuItem value={CONTACT_TYPE.FEEDBACK}>{t("profile.contact.type-feedback")}</MenuItem>
                        </Select>
                    </FormControl>
                    {isFeedback && (
                        <FormControl size="small" fullWidth required error={errApp} sx={commonSx}>
                            <InputLabel id="contact-app-label">{t("profile.contact.app")}</InputLabel>
                            <Select
                                labelId="contact-app-label"
                                id="selContactApp"
                                label={t("profile.contact.app")}
                                value={contactApp}
                                displayEmpty
                                onChange={(e) => {
                                    setContactApp(e.target.value);
                                    setErrApp(false);
                                }}
                            >
                                <MenuItem value="">{t("profile.contact.app-select")}</MenuItem>
                                {CONTACT_APP_IDS.map((id) => {
                                    const icon = resolveAppIconSrc(id);
                                    return (
                                        <MenuItem key={id} value={id}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                {icon ? (
                                                    <Box
                                                        component="img"
                                                        src={icon}
                                                        alt=""
                                                        sx={{
                                                            width: 24,
                                                            height: 24,
                                                            borderRadius: "22%",
                                                            objectFit: "cover",
                                                        }}
                                                    />
                                                ) : null}
                                                {APP_SITE_REGISTRY[id].displayName}
                                            </Box>
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                            {errApp ? (
                                <FormHelperText>{t("profile.contact.err-app-required")}</FormHelperText>
                            ) : null}
                        </FormControl>
                    )}
                    <TextField
                        id="txtSubject"
                        size="small"
                        label={t("profile.contact.subject")}
                        variant="outlined"
                        fullWidth
                        required
                        value={subject}
                        onChange={(e) => {
                            setSubject(truncateToWordLimit(e.target.value, MAX_SUBJECT_WORDS));
                            setErrSubject(false);
                        }}
                        error={errSubject}
                        helperText={t("profile.contact.field-word-count", {
                            current: countWords(subject),
                            max: MAX_SUBJECT_WORDS,
                        })}
                        FormHelperTextProps={{ sx: { textAlign: "right", mt: 0.5 } }}
                        sx={commonSx}
                    />
                    <TextField
                        id="txtContent"
                        size="small"
                        label={t("profile.contact.content")}
                        variant="outlined"
                        fullWidth
                        required
                        multiline
                        rows={isAuthenticated ? undefined : 5}
                        value={content}
                        onChange={(e) => {
                            setContent(truncateToWordLimit(e.target.value, MAX_CONTENT_WORDS));
                            setErrContent(false);
                        }}
                        error={errContent}
                        helperText={t("profile.contact.field-word-count", {
                            current: countWords(content),
                            max: MAX_CONTENT_WORDS,
                        })}
                        FormHelperTextProps={{ sx: { textAlign: "right", mt: 0.5 } }}
                        sx={{
                            ...commonSx,
                            flexGrow: 1,
                            display: "flex",
                            flexDirection: "column",
                            "& .MuiInputBase-root": {
                                flexGrow: 1,
                                alignItems: "flex-start",
                                height: isAuthenticated ? "100%" : "auto",
                            },
                            "& textarea": {
                                height: isAuthenticated ? "100% !important" : "auto",
                                overflow: "auto !important",
                            },
                        }}
                    />
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={handleSend}
                        sx={{
                            backgroundColor: "#3BA666",
                            color: "white",
                            textTransform: "none",
                            fontWeight: 600,
                            py: 1.5,
                            "&:hover": {
                                backgroundColor: "#2e7d32",
                            },
                        }}
                    >
                        {t("profile.contact.send-btn")}
                    </Button>
                    </Grid>
                    <Grid
                    size={{ xs: 12, md: 7 }}
                    sx={{
                        color: TEXT.primary,
                        backgroundColor: SURFACE.card,
                        display: "flex",
                        justifyContent: "flex-start",
                        textAlign: "left",
                        p: 3,
                        borderRadius: 2,
                        boxShadow: 1,
                    }}
                >
                    <Box sx={{ width: "100%" }}>
                        <Heading>
                            {isFeedback && selectedApp
                                ? t("profile.contact.get-in-touch-feedback", { appName: selectedApp.displayName })
                                : t("profile.contact.get-in-touch", { siteName: process.env.REACT_APP_SITE_NAME })}
                        </Heading>
                        <Subtitle>
                            {isFeedback && selectedApp
                                ? t("profile.contact.subtitle-feedback", { appName: selectedApp.displayName })
                                : t("profile.contact.subtitle", { siteName: process.env.REACT_APP_SITE_NAME })}
                        </Subtitle>
                    </Box>
                    </Grid>
                </Grid>

                <CustomSnackbar open={openSnackbar} setOpen={setOpenSnackbar} severity={severity} message={message} />
            </Box>
        </Box>
    );
}
